import { useState, useEffect } from "react";
import { Clock, Mail } from "lucide-react";
import { useAppSelector } from "@packages/store/hooks";
import { Trans, useTranslation } from "react-i18next";
import { getEnvironment } from "@packages/utils";
import i18n from "@packages/i18n/config";

const STORAGE_KEY = "madlen_trial_warning_modal_shown";

const TrialWarningModal = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const proDate = useAppSelector(
    (state) => state.user.teacher?.preferences?.teacherProDate,
  );
  const isLoggedIn = useAppSelector((state) => state.user.token);

  useEffect(() => {
    if (!isLoggedIn || !proDate) return;

    const hasBeenShown = localStorage.getItem(STORAGE_KEY);
    if (hasBeenShown) return;

    const now = new Date();
    const expirationDate = new Date(proDate);
    const daysRemaining =
      (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

    if (daysRemaining < 8 && daysRemaining >= 0) {
      setIsModalOpen(true);
    }
  }, [proDate, isLoggedIn]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setIsModalOpen(false);
  };

  if (!isModalOpen) {
    return null;
  }

  const expirationDate = proDate
    ? new Date(proDate).toLocaleDateString(i18n.language, {
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative max-w-xl w-full mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
        <div className="relative bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 p-6 text-white">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-white/10 rounded-full blur-2xl"></div>
          <div className="relative flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {t("trialWarningModal.header.title")}
              </h2>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
              <Trans
                i18nKey="trialWarningModal.message"
                values={{ date: expirationDate }}
                components={{
                  bold: <strong className="font-bold underline" />,
                }}
              />
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4">
              <p className="text-blue-800 dark:text-blue-200 text-sm text-center">
                <span className="font-medium">
                  {t("trialWarningModal.contactInfo")}
                </span>
              </p>
              <p className="text-blue-800 dark:text-blue-200 text-center mt-4">
                <a
                  href="mailto:hello@madlen.io"
                  className="text-blue-500 underline"
                >
                  hello@madlen.io
                </a>
              </p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <button
              onClick={handleClose}
              className="w-full bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              {t("trialWarningModal.footer.understood")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialWarningModal;
